import common from '../common';
import Drawing from '../drawing/Drawing';
import DrawingSpine from '../drawing/DrawingSpine';
import DrawingTextureHack from '../drawing/DrawingTextureHack';
import Scene from './Scene';
import {translate, Text, localeFont, saveImage} from '../utils';
import ConfirmDialog from '../ui/ConfirmDialog';
import ParticleEmitter from '../ParticleEmitter';
import "pixi-heaven";
import SuccessDialog from "../ui/SuccessDialog";

export default class DrawingScene extends Scene {
    constructor(spineName, name, hasProp, bgRevealName, propExtraProperties, staticForegroundProps) {
        super();

        this._spineName = spineName;
        this._name = name;
        this._hasProp = hasProp;
        this._bgRevealName = bgRevealName;
        this._propExtraProperties = propExtraProperties;
        this._staticForegroundProps = staticForegroundProps;

        this._hud = null;
        this._camera = null;
        this._layer = null;
        this._interactLayer = null;
        this._drawing = null;
        this._isDrawing = false;
        this._spine = null;
        this._pencilSpine = null;
        this._propSpine = null;
        this._data = null;
        this._slots = [];
        this._slotIndex = -1; // default: -1
        this._redoButton = null;
        this._approveButton = null;
        this._groupcount = 0;
        this._originalTexture = null;
        this._drawingsound = null;
        this._bodygroups = {};
        this._totalslots = 0;
        this._currentgroup = 'body';
        // this.btnBack = null;
        this._muteButton = null;
        this._backButton = null;
        this.dialog = null;
        this._emitter = null;

        this._progressBarMask = null;

        this._eraserSpine = null;

        this._CMF = null;		// colour matrix filter for greyscale buttons

        this._bgMusic = null;
        this._completeSounds = ['music_complete_sting_00', 'music_complete_sting_01', 'music_complete_sting_02'];

        this._step = 1;

        this._magicOffset = -380;
        this._canvasScale = common.config.drawing.canvas_scale;
        this._paths = [];
    }

    init() {
        super.init();
        this.createUI();

        this._CMF = new PIXI.filters.ColorMatrixFilter();
        this._CMF.desaturate();

        this._layer = new PIXI.Container();
        this.addChild(this._layer);

        this._interactLayer = new PIXI.Container();
        this._interactLayer.interactive = true;
        this.addChild(this._interactLayer);

        this._camera = new P3.Camera(
            new PIXI.Point(0, 0),
            new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5)
        );
        this._camera.zoom = 1;
        this._camera.addLayer(new P3.CameraLayer(this._layer, new PIXI.Point(1, 1)));

        this._drawing = new Drawing();
        this._drawing.signals.beginPath.add(this.onBeginPath, this);
        this._drawing.signals.endPath.add(this.onEndPath, this);
        this._drawing.init();
        this._layer.addChild(this._drawing);

        this._data = P3.assets.json(this._spineName + "_data");
        // console.log(this._data);

        this._holder = new PIXI.Container();
        this._holder.position.set(common.STAGE_WIDTH * 0.5 + this._data.spine.position.x, this._data.spine.position.y);
        this._layer.addChild(this._holder);

        const paper = new PIXI.NineSlicePlane(P3.assets.texture("paper"), 16, 16, 16, 40);
        paper.width = 990;
        paper.height = 1000;
        paper.pivot.set(paper.width * 0.5, paper.height * 0.5);
        paper.scale.set(this._canvasScale + 0.3);
        paper.y = this._magicOffset;
        this._holder.addChild(paper);
   
        this._polaroid = new PIXI.Sprite(P3.assets.texture(this._bgRevealName));
        this._polaroid.anchor.set(0.5);
        this._polaroid.scale.set(0.75 * this._canvasScale);
        this._polaroid.x = -2;
        this._polaroid.y = -15 + this._magicOffset;
        this._polaroid.visible = false;
        this._holder.addChild(this._polaroid);
   
        const tape = new PIXI.Sprite(P3.assets.texture("tape"));
        tape.anchor.set(0.5);
        tape.y = -510 * (this._canvasScale + 0.3) + this._magicOffset;
        this._holder.addChild(tape);

        this._paper = paper;
        this._tape = tape;

        // const prop = new PIXI.Sprite(P3.assets.texture("ivandoe_props"));
        // prop.anchor.set(0.5);
        // prop.position.set(0, 0);
        // this._holder.addChild(prop);

        const spineName = this._data.spine.name+"_"+this._name;
        this._spine = new DrawingSpine(P3.assets.spine(spineName), spineName, this._data.spine.texName);
        this._spineName2 = spineName;
        this._spine.scale.set(this._data.spine.scale);
        this._spine.position.set(this._data.spine.position.x, this._data.spine.position.y);
        this._spine.autoUpdate = false;
        this._spine.stateData.defaultMix = 0.2;
        this._spine.update(window.deltaTime);
        this._holder.addChild(this._spine);

        this._pencilSpine = new PIXI.spine.Spine(P3.assets.spine(spineName + '_pencil'));
        this._pencilSpine.scale.set(this._data.spine.scale);
        this._pencilSpine.autoUpdate = false;
        this._pencilSpine.stateData.defaultMix = 0.2;
        this._pencilSpine.update(window.deltaTime);
        this._pencilSpine.position.set(common.STAGE_WIDTH * 0.5 + (this._data.spine.position.x * 2), this._data.spine.position.y - Math.abs(this._data.spine.position.y)); 
        
        this._layer.addChild(this._pencilSpine);

        if (this._hasProp) {
            this._propSpine = new PIXI.spine.Spine(P3.assets.spine(spineName + '_prop'));
            this._propSpine.scale.set(this._spine.scale.x);
            this._propSpine.autoUpdate = false;
            this._propSpine.stateData.defaultMix = 0.2;
            this._propSpine.update(window.deltaTime);
            this._propSpine.alpha = 0;

            this._propSpine.position.set(common.STAGE_WIDTH * 0.5, this._spine.y);
            if (this._propExtraProperties)
                this._propSpine.y += this._propExtraProperties.propYoffset;
            this._layer.addChild(this._propSpine);
        }

        this._currentAnimation = null;

        this._pencil = this._pencilSpine.skeleton.findSlot('guide_pencil');

        this._fadetween = null;

        this._pencilSpine.state.addListener({
            start: () => {
                //  this._pencil.sprites.guide_pencil.alpha = this._isDrawing ? 0.5 : 1;
            },
            complete: () => {
                //const position = {x: this._pencil.sprites.guide_pencil.x, y: this._pencil.sprites.guide_pencil.y};
                this._pencilSpine.state.clearTrack(0);
                this._pencilSpine.state.setAnimation(0, this._currentAnimation, false);
                return;
                this._fadetween = new P3.utils.TWEEN.Tween(this._pencil.sprites.guide_pencil, this.tweens)
                    .to({
                        alpha: 0
                    }, 2000)
                    .easing(P3.utils.TWEEN.Easing.Quartic.Out)
                    .onComplete(() => {
                        this._pencilSpine.state.clearTrack(0);
                        this._pencilSpine.state.setAnimation(0, this._currentAnimation, false);
                        //this._pencil.sprites.guide_pencil.alpha = this._isDrawing ? 0.3 : 1;
                        //this._pencil.sprites.guide_pencil.x = position.x;
                        //this._pencil.sprites.guide_pencil.y = position.y;
                    })
                    .start();
            }
        }, this);

        this._camera.setTarget(new PIXI.Point(this._holder.x - this._holder.x, this._holder.y - 600));

        let mask = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 0.4);
        mask.drawRect(350, 60, 950, 630);
        mask.endFill();
        // this.addChild(mask);
        //this._holder.mask = mask;

        this.parseData(this._data);
        this.initProgressBar();

        let states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_medium_out');
        states.over = P3.assets.texture('btn_primary_medium_over');
        states.down = P3.assets.texture('btn_primary_medium_down');
        states.icon = P3.assets.texture('icon_undo');

        this._redoButton = new P3.Button(states);
        this._redoButton.signals.click.add(() => {
            /* this._eraserSpine.visible = true;
            this._eraserSpine.state.setAnimation(0, 'undo', false);
            P3.audio.playSound('sfx_btn_ui_repeatstep_00');
            this._groupcount = 0;
            this.nextSlot(true);

            this._approveButton.enabled = this._redoButton.enabled = false;
            this._approveButton.alpha = this._redoButton.alpha = 0.7;
            this._approveButton.filters = [this._CMF]; */
            if (this.dialog.visible) return;
            this.dialog.visible = true;

            this.dialog.signals.confirm.once(() => {
                this._dialogOpen = false;
                //  this._eraserSpine.visible = true;
                // this._eraserSpine.state.setAnimation(0, 'undo', false);
                P3.audio.playSound('sfx_btn_ui_repeatstep_00');
                this._groupcount = 0;
                this.nextSlot(true);

                this._approveButton.enabled = this._redoButton.enabled = false;
                this._approveButton.alpha = this._redoButton.alpha = 0.7;
                this._approveButton.filters = [this._CMF];
                this.dialog.visible = false;
            }, this);
            this.dialog.signals.deny.once(() => {
                this.dialog.visible = false;
            });
        }, this);
        this._redoButton.enabled = false;
        this._redoButton.alpha = 0.7;
        this._redoButton.position.set(common.STAGE_WIDTH * 0.5 + 300, common.STAGE_HEIGHT - 75);
        this.addChild(this._redoButton);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_large_out');
        states.over = P3.assets.texture('btn_primary_large_over');
        states.down = P3.assets.texture('btn_primary_large_down');// P3.assets.texture('btn_primary_large_down');
        states.icon = P3.assets.texture('icon_play');

        this._approveButton = new P3.Button(states);
        this._approveButton.signals.click.add(this.onApproveButtonClick, this);
        this._approveButton.position.set(common.STAGE_WIDTH * 0.5 + 445, common.STAGE_HEIGHT - 100);
        this._approveButton.scale.set(0.9);

        this._approveButton.enabled = false;
        this._approveButton.filters = [this._CMF];
        this._approveButton.alpha = 0.7;

        this.addChild(this._approveButton);

        this.addInteractListeners();

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_small_out');
        states.over = P3.assets.texture('btn_primary_small_over');
        states.down = P3.assets.texture('btn_primary_small_down');
        states.icon = P3.assets.texture('icon_sound_on');
        states.iconAlt = P3.assets.texture('icon_sound_off');
        this._muteButton = new P3.MuteButton(states);
        this._muteButton.signals.up.add(function () {
            common.saveData.data.mute = !P3.audio.isMuted();
            common.saveData.save();
        }, this);
        this.addChild(this._muteButton);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_small_out');
        states.over = P3.assets.texture('btn_primary_small_over');
        states.down = P3.assets.texture('btn_primary_small_down');
        states.icon = P3.assets.texture('icon_levels');
        this._backButton = new P3.Button(states);
        this.addChild(this._backButton);

        this.dialog = new ConfirmDialog(translate('undo_confirm'));
        this.dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this.dialog.visible = false;
        this.addChild(this.dialog);

        this.dialog2 = new ConfirmDialog(translate('are_you_sure'));
        this.dialog2.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this.dialog2.visible = false;
        this.addChild(this.dialog2);

        this._backButton.signals.click.add(() => {
            if (this.dialog2.visible) return;

            this.dialog2.visible = true;

            this.dialog2.signals.confirm.once(() => {
                this._dialogOpen = false;
                //  this._eraserSpine.visible = true;
                // this._eraserSpine.state.setAnimation(0, 'undo', false);
                //P3.audio.playSound('sfx_btn_ui_repeatstep_00');
                this.signals.previous.dispatch(this);
                this.dialog2.visible = false;
            }, this);
            this.dialog2.signals.deny.once(() => {
                this.dialog2.visible = false;
            });
        }, this);

        /*
        this._emitter = new ParticleEmitter('rubberFX', [
            P3.assets.texture('particle_rubber_001'),
            P3.assets.texture('particle_rubber_002'),
            P3.assets.texture('particle_rubber_003'),
            P3.assets.texture('particle_rubber_004'),
            P3.assets.texture('particle_rubber_005'),
            P3.assets.texture('particle_rubber_006')
        ]);
        this._emitter.scale.set(4);
        this._emitter.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT);
        this.addChild(this._emitter);

         */

        /*
        this._eraserSpine = new PIXI.spine.Spine(P3.assets.spineData('rubberSpine'));
        this._eraserSpine.scale.set(0.5);
        this._eraserSpine.visible = false;
        this._eraserSpine.autoUpdate = false;
        this._eraserSpine.stateData.defaultMix = 0.2;
        this._eraserSpine.update(P3.Ticker.dt);
        this._eraserSpine.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this.addChild(this._eraserSpine);

        this._eraserSpine.state.addListener({
            start: () => {
               // this._emitter.play();
            },
            complete: () => {
               // this._emitter.stopEmission();
            }
        }, this);

         */

        this._camera.target.y -= 0.5;
        this._camera.zoom = 0.5;

        this._drawLock = true;

        this._successDialog = new SuccessDialog(translate("success"));
        this._successDialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._successDialog.visible = false;
        this.addChild(this._successDialog);

    }

    results() {
        let states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_large_out');
        states.over = P3.assets.texture('btn_primary_large_over');
        states.down = P3.assets.texture('btn_primary_large_down');// P3.assets.texture('btn_primary_large_down');
        states.icon = P3.assets.texture('icon_download');

        this._saveButton = new P3.Button(states);
        this._saveButton.signals.click.add(this.onSaveButton, this);
        this._saveButton.position.set(common.STAGE_WIDTH * 0.5 + 380, common.STAGE_HEIGHT - 110);
        this._saveButton.scale.set(0.9);
        this.addChild(this._saveButton);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_medium_out');
        states.over = P3.assets.texture('btn_primary_medium_over');
        states.down = P3.assets.texture('btn_primary_medium_down');// P3.assets.texture('btn_primary_large_down');
        states.icon = P3.assets.texture('icon_replay');

        this._replayButton = new P3.Button(states);
        this._replayButton.signals.click.add(this.onReplayButton, this);
        this._replayButton.position.set(common.STAGE_WIDTH * 0.5 + 235, common.STAGE_HEIGHT - 80);
        this._replayButton.scale.set(0.9);
        this.addChild(this._replayButton);

        this._logo = new PIXI.Sprite(P3.assets.texture("game_logo", ""));
        this._logo.anchor.set(0.5);
        this._logo.scale.set(0);
        this._logo.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - 274);
        //this._logo.rotation = -0.17;
        this.addChild(this._logo);

        /*this._labelPanelA = new PIXI.Sprite(P3.assets.texture("title_bg"));
        this._labelPanelA.anchor.set(0.5);
        this.addChild(this._labelPanelA);*/

        this._labelPanelB = new PIXI.Sprite(P3.assets.texture("title_1_bg"));
        this._labelPanelB.anchor.set(0.5);
        this.addChild(this._labelPanelB);

        /*this._labelA = new PIXI.Text(translate("complete_a"), {
            fontFamily: localeFont(),
            fontSize: 58,
            dropShadow: true,
            dropShadowAngle: 2.5,
            dropShadowColor: "#fe2e8b",
            fill: "#ffffff",
            padding: 5
        });
        this.addChild(this._labelA);*/

        this._labelB = new PIXI.Text(translate(this._spineName), {
            fontFamily: "Fred Fredburger", //localeFont(),
            fontSize: 45,
            dropShadow: true,
            dropShadowAngle: 2.5,
            dropShadowColor: "#fe2e8b",
            fill: "#fed917",
            padding: 5
        });
        this._labelB.anchor.set(0.5);
        this.addChild(this._labelB);

        //this._labelPanelA.position.set(common.STAGE_WIDTH * 0.5 - 240, common.STAGE_HEIGHT - 130);
        this._labelPanelB.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT - 95);

        //this._labelA.position = this._labelPanelA.position.clone();
        this._labelB.position = this._labelPanelB.position.clone();

        //this._labelPanelA.width = this._labelA.width + 60;
        this._labelPanelB.width = this._labelB.width + 50;

        //const targetDimensionsA = {width: this._labelPanelA.width, height: this._labelPanelA.height};
        const targetDimensionsB = {width: this._labelPanelB.width, height: this._labelPanelB.height};

        /*this._labelA.scale.set(0);
        new P3.utils.TWEEN.Tween(this._labelA.scale, this.tweens)
            .to({x: 1, y: 1}, 270)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        this._labelPanelA.scale.set(0);
        new P3.utils.TWEEN.Tween(this._labelPanelA, this.tweens)
            .to(targetDimensionsA, 270)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();*/

        this._logo.scale.set(0);
        new P3.utils.TWEEN.Tween(this._logo.scale, this.tweens)
            .to({x: 0.35, y: 0.35}, 270)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        this._labelB.scale.set(0);
        new P3.utils.TWEEN.Tween(this._labelB.scale, this.tweens)
            .to({x: 1, y: 1}, 270)
            .delay(100)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        this._labelPanelB.scale.set(0);
        new P3.utils.TWEEN.Tween(this._labelPanelB, this.tweens)
            .to(targetDimensionsB, 270)
            .delay(100)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        this._replayButton.scale.set(0);
        new P3.utils.TWEEN.Tween(this._replayButton.scale, this.tweens)
            .to({x: 0.9, y: 0.9}, 270)
            .delay(200)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        this._saveButton.scale.set(0);
        new P3.utils.TWEEN.Tween(this._saveButton.scale, this.tweens)
            .to({x: 0.9, y: 0.9}, 270)
            .delay(300)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();
    }

    animateIn() {
        super.animateIn();
        //P3.utils.delayg(this._tweens, 0.5, () => this.nextSlot(false));
        this.nextSlot(false);
    }

    initProgressBar() {

        this._progressBar = new PIXI.Container();
        this.addChild(this._progressBar);
        const groups = Object.keys(this._bodygroups);

        // bars & mask init
        let barbg = new PIXI.Sprite(P3.assets.texture('progress_bg'));
        barbg.anchor.set(0.5);
        //  barbg.position.set(common.STAGE_WIDTH * 0.5 - 100, common.STAGE_HEIGHT - 75);
        this._progressBar.addChild(barbg);

        let barbgfill = new PIXI.Sprite(P3.assets.texture('progress_fill'));
        barbgfill.anchor.set(0.5);
        //  barbgfill.position.set(common.STAGE_WIDTH * 0.5 - 100, common.STAGE_HEIGHT - 75);
        this._progressBar.addChild(barbgfill);

        let dividergroup = new PIXI.Container();
        // for (let i = 0; i < groups.length - 1; i++) {
        //let bardivider = new PIXI.Sprite(P3.assets.texture('progress_bar_divider'));
        //bardivider.anchor.set(0.5);
        ///bardivider.position.set(((i * barbg.width) / groups.length), 0);			//barbg.x + (barbg.width * -0.5) + (i + 1) * 140, barbg.y
        // dividergroup.addChild(bardivider);
        // }
        this._progressBar.addChild(dividergroup);
        dividergroup.position.set(barbg.x - dividergroup.width * 0.5 + 2, barbg.y);

        this._progressBarMask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._progressBarMask.width = 0;
        this._progressBarMask.height = barbgfill.height;
        this._progressBarMask.anchor.set(0, 0.5);
        this._progressBarMask.x = -barbgfill.width * 0.5;
        this._progressBar.addChild(this._progressBarMask);
        barbgfill.mask = this._progressBarMask;

        let index = 0;
        let textgroup = new PIXI.Container();
        for (const group in this._bodygroups) {
            const g = this._bodygroups[group];

            /*
            let groupLabel = new Text(translate(`${group}`), {
                fontSize: 26,
                fontFamily: 'Ed',
                align: 'center',
                colour: 0xFFFFFF
            });
            groupLabel.anchor.set(0.5, 0.5);
            groupLabel.position.set((index * barbg.width) / groups.length, 0);
            //textgroup.addChild(groupLabel);


            if (index !== 0) groupLabel.x -= 5;
            if (groups.length === 1) groupLabel.x += 80;
            if (groups.length === 2 && index === 0) groupLabel.x -= 100;
            if (groups.length === 2 && index === 1) groupLabel.x -= 120;
            if (groups.length === 5 && index === 4) groupLabel.x -= 10;

*/
            this._totalslots += g;
            index++;
        }

        textgroup.position.set(barbg.x - dividergroup.width * 0.5 - 65, barbg.y);
        this.addChild(textgroup);
    }

    setProgress(sub = false) {
        this._step += sub ? -1 : 1;

        const barWidth = 360;
        this._progressBarMask.width = barWidth / this._bodygroups[this._currentgroup] * this._step;

        //if (sub) {
        //  this._progressBarMask.width = this._progressBar.width / this._bodygroups[this._currentgroup] / Object.keys(this._bodygroups).length;
        //this._progressBarMask.x -= (this._progressBarMask.width / this._bodygroups[this._currentgroup]) / Object.keys(this._bodygroups).length;
        //   return;
        //   }

        // this._progressBarMask.x += (this._progressBarMask.width / this._bodygroups[this._currentgroup]) / Object.keys(this._bodygroups).length;
    }

    destroy() {
        this.removeInteractListener();
        this._drawing.destroy();

        //  P3.audio.stopMusic();

        super.destroy();
    }

    end() {
        if (!this._progressBar.visible)
            return;

        new P3.utils.TWEEN.Tween(this._holder, this.tweens)
            .to({x: common.STAGE_WIDTH * 0.5, y: this._holder.y}, 270)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .start();

        const sound = P3.utils.selectRandom(this._completeSounds);
        // this._bgMusic.stop();

        this._pencilSpine.visible = false;
        this._progressBar.visible = false;
        this._approveButton.visible = false;
        this._redoButton.visible = false;
        // this._paper.visible = false;
        //  this._tape.visible = false;

        this._polaroid.alpha = 0.2;
        new P3.utils.TWEEN.Tween(this._polaroid, this.tweens)
            .to({alpha: 1}, 700)
            .delay(310)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .onStart(() => {
                this._polaroid.visible = true;
            })
            .start();

        // this._spine.state.setAnimation(0, "bg_reveal", false);
        this._spine.state.setAnimation(0, "final_1", true);
        this.tweenPropSpine();

        // animate camera
        new P3.utils.TWEEN.Tween(this._camera.target, this.tweens)
            .to({
                x: 0.,
                y: this._data.spine.position.y - common.STAGE_HEIGHT * 0.95
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onComplete(() => {
                //this._drawing.enabled = true;
            })
            .start();

        new P3.utils.TWEEN.Tween(this._camera, this.tweens)
            .to({
                zoom: 0.38
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();

        P3.audio.stopMusic();
        P3.audio.playSound(sound, {
            callback: () => {
                P3.audio.playMusic('music_drawing_loop_00');
                this.results();
                //  P3.utils.delayg(this._tweens, 1.5, () => this.signals.next.dispatch(this));
            },
            scope: this
        });

        //common.saveData.saveSpineHacks("gumball_darwin_00", this._spine);
    }

    createUI() {
        let bg = new PIXI.Sprite(P3.assets.texture('bg_main'));
        // bg.anchor.set(0.5);
        //  bg.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        bg.width = common.STAGE_WIDTH;
        bg.height = common.STAGE_HEIGHT;
        this.addChild(bg);

        //let frame = new PIXI.Sprite(P3.assets.texture('frame'));
        //frame.anchor.set(0.5, 0);
        // frame.position.set(common.STAGE_WIDTH * 0.5, 0);
        // this.addChild(frame);
    }

    update(dt = window.deltaTime) {
        super.update();
        this._camera.update();
        this._drawing.update();

        this._spine.update(dt);
        this._pencilSpine.update(dt);
        //   this._eraserSpine.update(P3.Ticker.dt);
        //  this._emitter.update();

        if (this._isDrawing) {
            this._backButton.enabled = false;
        } else {
            this._backButton.enabled = true;
        }

        if (P3.input.isKeyPressed(P3.keys.E)) {
            //  this._camera.zoom += 0.05;
            // console.log(this._camera.zoom);
        }
        if (P3.input.isKeyPressed(P3.keys.Q)) {
            //   this._camera.zoom -= 0.05;
            // console.log(this._camera.zoom);
        }

        //TODO Debug -- do not leave this in the final build!
        // if (P3.input.isKeyPressed(P3.keys.Z)) {
        //     this.end();
        // }

        if (P3.input.isKeyPressed(P3.keys.M)) {
            this._spine.state.setAnimation(0, "final_1", true);
            this._spine.update(0);
            this.tweenPropSpine();
        }
    }

    resize() {
        super.resize();
        this._drawing.resize();

        this._interactLayer.hitArea = new PIXI.Rectangle(0, 0, common.STAGE_WIDTH, P3.View.height);
        //  this.btnBack.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 100, 100);
        this._muteButton.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 100, 100);

        this._progressBar.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT - 60);

        this._redoButton.position.set(common.STAGE_WIDTH * 0.5 + 250, common.STAGE_HEIGHT - 80);
        this._approveButton.position.set(common.STAGE_WIDTH * 0.5 + 400, common.STAGE_HEIGHT - 110);

        this._labelPanelA && this._labelPanelA.position.set(common.STAGE_WIDTH * 0.5 - 240, common.STAGE_HEIGHT - 130);
        this._labelPanelB && this._labelPanelB.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT - 95);

        this._backButton.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 220, 100);
    }

    parseData(data) {
        this._slots.length = 0;

        for (const i in data.slots) {
            if (data.slots.hasOwnProperty(i)) {
                const t = P3.utils.copyObject(data.slots[i], true);
                t.name = i;

                if (!this._bodygroups[t.bodygroup])
                    this._bodygroups[t.bodygroup] = 0;

                this._bodygroups[t.bodygroup]++;

                this._slots.push(t);
            }
        }
        this._slots.sort((a, b) => a.order - b.order);
    }

    randomText() {
        const messages = [
            //'encouragement_message1',
            'encouragement_message2',
            'encouragement_message3',
            'encouragement_message4',
            'encouragement_message5'
        ];

        if (this._step > 6) {
            messages.push('encouragement_message1');
        }

        const randomMessage = P3.utils.selectRandom(messages);

        const offset = Math.random() > 0.5 ? 200 : -200;

        let motivationalText = new PIXI.Text(translate(`${randomMessage}`), {
            "dropShadow": true,
            "dropShadowAngle": 2.4,
            "dropShadowColor": ["#ff40ff", "#fff200", "#009bdf"],
            "dropShadowDistance": 10,
            "fill": "#feffff",
            "fontFamily": "Fred Fredburger", //localeFont(),
            "fontSize": 61,
            "padding": 9,
            "strokeThickness": 6,
            "lineJoin": "round"
        });

        /*
        {
            "dropShadow": true,
            "dropShadowAngle": 2.4,
            "dropShadowColor": "#ff40ff",
            "dropShadowDistance": 10,
            "fill": "#feffff",
            "fontFamily": "Impact",
            "fontSize": 61,
            "padding": 9,
            "strokeThickness": 6
        }

         */
        motivationalText.anchor.set(0.5);
        motivationalText.position.set(common.STAGE_WIDTH * 0.5 + offset, common.STAGE_HEIGHT * 0.5 - 200);
        this.addChild(motivationalText);

        const randomX = common.STAGE_WIDTH * 0.5 + offset + P3.math.randomInt(-100, 100);
        new P3.utils.TWEEN.Tween(motivationalText, this.tweens)
            .to({
                x: randomX, y: 0
            }, 3000)
            .easing(P3.utils.TWEEN.Easing.Quadratic.In)
            .onComplete(() => {
                motivationalText.destroy();
            })
            .start();

        new P3.utils.TWEEN.Tween(motivationalText, this.tweens)
            .to({
                alpha: 0
            }, 1000)
            .easing(P3.utils.TWEEN.Easing.Quadratic.In)
            .delay(2000)
            .start();
    }

    nextSlot(repeat = false) {
        
        const t = repeat ? this._slots[this._slotIndex] : this._slots[++this._slotIndex];
        //console.log(this._spine.skeleton);
        let slot = this._spine.skeleton.findSlot(t.name);
        const order = P3.utils.padNum(t.order + 1, 3);
        this._redoButton.filters = [this._CMF];
        this._redoButton.enabled = false;
        // set drawing style
        this._drawing.style = t.style;
        this._drawing.beginSlot(t.boolean, t.name);
        this._drawing.enabled = true;
        this._drawing.grouped = t.grouped;
        this._currentgroup = t.bodygroup;

        //console.log(t.name);
       // console.log(slot);

        // console.log(t.name);

        if (Math.random() > 0.3 && this._slotIndex !== 0)
            this.randomText();

        if (repeat) {
            this.setProgress(true);

            let inTween = new P3.utils.TWEEN.Tween(slot.currentSprite, this.tweens)
                .to({
                    alpha: 1
                }, 300)
                .delay(1000)
                .onUpdate((v) => {
                    this._pencilSpine.alpha = v.alpha;
                })
                .easing(P3.utils.TWEEN.Easing.Linear.None);

            new P3.utils.TWEEN.Tween(slot.currentSprite, this.tweens)
                .to({
                    alpha: 0
                }, 800)
                .easing(P3.utils.TWEEN.Easing.Linear.None)
                .onUpdate((v) => {
                    this._pencilSpine.alpha = v.alpha;
                })
                .onComplete(() => {
                    slot.currentSprite.anchor.set(0.5);
                    this._spine.hackTextureBySlotName(t.name, this._originalTexture, this._originalTexture.orig);
                    inTween.start();
                })
                .start();
        }

        const guideSlotName = `${t.name}_guide`;

        //console.log(guideSlotName);

        const guide = this._spine.skeleton.findSlot(guideSlotName);
        // console.log(guide);
        if (guide) {
            const guideSprite = guide.currentSprite;
            guideSprite && (guideSprite.visible = true);
        }

        this._fadetween && this._fadetween.stop();

        // console.log(guideSlotName);

        //console.log(slot);

        // hide the slot whilst we are drawing
        const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh ? slot.currentMesh : null;
        sprite && (sprite.visible = true);
        sprite && (sprite.alpha = this._isDrawing ? 0.5 : 1);

        // display the guide
        this._spine.state.setAnimation(0, `${order}_${t.name}_guide`, false);
        this._pencilSpine.state.setAnimation(0, `${order}_${t.name}_guide`, false);
        this._pencilSpine.alpha = 1;
        this._currentAnimation = `${order}_${t.name}_guide`;

        let localPos = this._layer.toLocal(new PIXI.Point(sprite.x, sprite.y), slot.currentSprite.parent);

        // animate camera
        new P3.utils.TWEEN.Tween(this._camera.target, this.tweens)
            .to({
                x: localPos.x - common.STAGE_WIDTH * 0.5,
                y: localPos.y - common.STAGE_HEIGHT * 0.5
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onComplete(() => {
                //this._drawing.enabled = true;
                this._drawLock = false;
            })
            .start();

        new P3.utils.TWEEN.Tween(this._camera, this.tweens)
            .to({
                zoom: t.camera.zoom
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();

        this._spine.update(0);
    }

    addInteractListeners() {
        this._interactLayer.on('mousedown', this._drawing.onPointerDown, this._drawing);
        this._interactLayer.on('mousemove', this._drawing.onPointerMove, this._drawing);
        this._interactLayer.on('mouseup', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.on('moouseupoutside', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.on('touchstart', this._drawing.onPointerDown, this._drawing);
        this._interactLayer.on('touchmove', this._drawing.onPointerMove, this._drawing);
        this._interactLayer.on('touchend', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.on('touchendoutside', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.on('pointerupoutside', this._drawing.onPointerUp, this._drawing);
    }

    removeInteractListener() {
        this._interactLayer.off('mousedown', this._drawing.onPointerDown, this._drawing);
        this._interactLayer.off('mousemove', this._drawing.onPointerMove, this._drawing);
        this._interactLayer.off('mouseup', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.off('moouseupoutside', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.off('touchstart', this._drawing.onPointerDown, this._drawing);
        this._interactLayer.off('touchmove', this._drawing.onPointerMove, this._drawing);
        this._interactLayer.off('touchend', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.off('touchendoutside', this._drawing.onPointerUp, this._drawing);
        this._interactLayer.off('pointerupoutside', this._drawing.onPointerUp, this._drawing);
    }

    onBeginPath() {

        if (this._drawLock)
            return;

        this._isDrawing = true;

        this._drawingsound && this._drawingsound.stop();
        this._drawingsound = P3.audio.playSound('sfx_drawing_loop_00', {loop: true});

        new P3.utils.TWEEN.Tween(this._pencilSpine, this.tweens)
            .to({
                alpha: 0
            }, 150)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();
    }

    onEndPath(source, path) {

        if (this._drawLock)
            return;

        this._drawingsound && this._drawingsound.stop();
        P3.audio.playSound('sfx_drawing_fill_00');

        if (this._slots[this._slotIndex].grouped && ++this._groupcount < this._slots[this._slotIndex].groupcount) return; // If we have a slot with multiple lines, e.g. whiskers or eye lashes, we let the user draw all of those first.

        this._groupcount = 0;
        this._drawing.grouped = false;
        this._drawing.enabled = false;

        this.setProgress();

        if (path) {
            this._isDrawing = false;
            const t = this._slots[this._slotIndex];
            // const order = P3.utils.padNum(t.order + 1, 3);
            let slot = this._spine.skeleton.findSlot(t.name);

            //console.log(`${t.name}_guide`);

            const guide = this._spine.skeleton.findSlot(`${t.name}_guide`);
            //console.log(guide);

            //console.log(guide.currentSprite);
            const center = this._layer.toLocal(guide.currentSprite.position, guide.currentSprite.parent);

            path.name = t.name;
            this._paths.push(path);

            //  console.log(slot.sprites);
            this._originalTexture = slot.sprites["empty_texture"].texture.clone();
            guide.setAttachment(null);

            const hack = DrawingTextureHack.create(path, slot, center);
            this._spine.addTextureHack(hack);

           // common.saveData.saveSpineHacks(this._spineName2, this._spine);

            // show the slot that was previously hidden
            slot = this._spine.skeleton.findSlot(t.name);
            const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh ? slot.currentMesh : null;
            sprite && (sprite.visible = true);

            if (slot.currentSprite) {
                // console.log("Sprite:");
                // console.log(common.renderer.extract.pixels(sprite));
            }

            const guideSprite = guide.currentSprite;
            guideSprite && (guideSprite.visible = false);

            source.enabled = false;

            this._approveButton.alpha = this._redoButton.alpha = 1;
            this._approveButton.enabled = this._redoButton.enabled = true;
            this._approveButton.filters = this._redoButton.filters = null;

            //  console.log("Sprite:");
            //  console.log(common.renderer.extract.pixels(sprite));
            //console.log(this._spine.skeleton.findSlot(this._drawing));

            //Use regular masking
            if (/* this._drawing.useRegularMasking*/false) {

                this._drawing.useRegularMasking = false;//toggle it back off otherwise whole drawing uses regular masking..

                if (this._spine.skeleton.findSlot(this._drawing.booleanDest)) {
                    const currentSprite = this._spine.skeleton.findSlot(this._drawing.booleanDest).currentSprite;

                    let booleanPath;
                    for (let i = 0; i < this._paths.length; i++) {
                        if (this._paths[i].name === this._drawing.booleanDest) {
                            booleanPath = this._paths[i];
                            break;
                        }
                    }

                    if (booleanPath) {
                        console.log("Boolean path found");
                        booleanPath.fillColor = '#FFFFFF';

                        const raster = booleanPath.rasterize();
                        let maskTexture = new PIXI.Texture.from(raster.canvas);
                        //const booleanPathChild = booleanPath.children[0];
                        //booleanPathChild.style.fillColor = '#AFFF3D';

                        const maskSprite = new PIXI.Sprite(maskTexture);
                        // maskSprite.renderable = false;

                        maskSprite.anchor.set(currentSprite.anchor.x, currentSprite.anchor.y);
                        //maskSprite.scale.set(currentSprite.scale.x, currentSprite.scale.y);
                        //maskSprite.rotation = currentSprite.rotation;
                        //maskSprite.position.set(currentSprite.x, currentSprite.y);
                        currentSprite.addChild(maskSprite);

                        // if (this._spine.skeleton.findSlot(t.name).currentSprite.mask)
                        //   this._spine.skeleton.findSlot(t.name).currentSprite.mask.destroy();

                        this._spine.skeleton.findSlot(t.name).currentSprite.mask = maskSprite;
                    }
                }
            }
        }
    }

    onApproveButtonClick(e) {
        this._drawing.onConfirm();
        P3.audio.playSound('sfx_btn_ui_commit_00');
        if (this._slotIndex === this._slots.length - 1) {
            this.end();
        } else {
            this._groupcount = 0;
            this.nextSlot(false);
        }

        this._approveButton.enabled = this._redoButton.enabled = false;
        this._approveButton.alpha = this._redoButton.alpha = 0.7;
        this._approveButton.filters = [this._CMF];
    }

    onBackButtonClick() {
        this.signals.previous.dispatch(this);
    }

    onReplayButton(button) {
        this.signals.next.dispatch(this);
    }

    onSaveButton() {
        /*
        const saveImage = new PIXI.Container();
        const logo = new PIXI.Sprite(P3.assets.texture("logo"));
        logo.anchor.set(0);
        logo.scale.set(0.5);
        logo.position.set(-this._polaroid.width * 0.5+30, -this._polaroid.height+80);
        saveImage.addChild(this._polaroid);
        saveImage.addChild(this._spine);
        saveImage.addChild(logo);
        */

        //this.addChild(saveImage);

        if (!common.config.delay_before_photo) {
            this.saveImage();
        } else {

            this._spine.state.setAnimation(0, "final_1", true);
            this._spine.update(0);
            this.tweenPropSpine();

            P3.utils.delayg(this._tweens, common.config.delay_before_photo_time, () => {
                this.saveImage();
            });
        }

        /*
                this._holder.addChild(this._polaroid);
                this._holder.addChild(this._spine);
                this._holder.addChild(this._tape);
                saveImage.destroy();
                */

    }

    tweenPropSpine() {
        if (this._propSpine) {
            new P3.utils.TWEEN.Tween(this._propSpine, this._tweens)
                .to({alpha: 1}, 800)
                .start();
        }
    }

    saveImage() {
        const logo = new PIXI.Sprite(P3.assets.texture("game_logo", ""));
        logo.anchor.set(0);
        logo.scale.set(0.3);

        this._logo.visible = false;

        //this._labelPanelA.visible = false;
        this._labelPanelB.visible = false;

        //this._labelA.visible = false;
        this._labelB.visible = false;

        this._saveButton.visible = false;
        this._replayButton.visible = false;
        this._backButton.visible = false;

        const padding = 100;
        const bounds = this._polaroid.getBounds();

        bounds.width += padding;
        bounds.height += padding;
        bounds.x -= padding * 0.5;
        bounds.y -= padding * 0.5;

        logo.position.set(bounds.x + 10 - this.x, bounds.y + 10 - this.y);
        this.addChild(logo);
        logo.updateTransform();

        let logoCn;
        if (common.config.drawing.include_logo_cn) {
            logoCn = new PIXI.Sprite(P3.assets.texture("cn"));
            logoCn.anchor.set(1);
            logoCn.position.set(bounds.x + bounds.width - 30 - this.x, bounds.y + bounds.height - 30 - this.y);
            this.addChild(logoCn);
            logoCn.updateTransform();
        }

        const texture = common.renderer.generateTexture(this, PIXI.SCALE_MODES.LINEAR, 1, bounds);
        saveImage(texture, () => {
            // display success msg
            this._successDialog.visible = true;
            P3.utils.delayg(this._tweens, 2, () => this._successDialog.visible = false);
        });
        texture.destroy(true);

        this._logo.visible = true;

        //this._labelPanelA.visible = true;
        this._labelPanelB.visible = true;

        //this._labelA.visible = true;
        this._labelB.visible = true;

        this._saveButton.visible = true;
        this._replayButton.visible = true;
        this._backButton.visible = true;

        logo.destroy();

        if (common.config.drawing.include_logo_cn && logoCn)
            logoCn.destroy();
    }
}
